import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, CardBody, LoadingBlock, PageHeader, TextField } from "@nahal/ui";
import { useMe, useUpdateOrganization, useOrganizationAccess } from "../../lib/queries";

export function OrganizationScreen() {
  const me = useMe();
  const { canManage } = useOrganizationAccess();
  const update = useUpdateOrganization();
  const [form, setForm] = useState({ name: "", legalName: "", country: "", billingEmail: "" });

  useEffect(() => {
    if (!me.data) return;
    setForm({
      name: me.data.organization.name,
      legalName: me.data.organization.legalName ?? "",
      country: me.data.organization.country ?? "",
      billingEmail: me.data.organization.billingEmail ?? "",
    });
  }, [me.data]);

  if (me.isLoading) return <LoadingBlock />;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canManage) return;
    update.mutate(form);
  }

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value })),
  });

  return (
    <>
      <PageHeader title="Organisation" description="How Nahal Labs bills and contacts you." />
      <Card>
        <CardBody>
          <form onSubmit={submit} className="max-w-xl space-y-4">
            {!canManage && <p className="text-sm text-[var(--text-secondary)]">Only an owner or administrator can edit organisation details.</p>}
            <fieldset disabled={!canManage || update.isPending} className="space-y-4">
              <TextField label="Name" required {...field("name")} />
              <TextField label="Legal name" hint="As registered, if different." {...field("legalName")} />
              <TextField label="Country" maxLength={2} hint="Two-letter code, e.g. BD." {...field("country")} />
              <TextField label="Billing email" type="email" inputMode="email" {...field("billingEmail")} />
            </fieldset>
            {canManage && <Button type="submit" variant="solid" size="lg" className="w-full sm:w-auto" disabled={update.isPending}>
                {update.isPending ? "Saving..." : "Save changes"}
            </Button>}
          </form>
        </CardBody>
      </Card>
    </>
  );
}
